import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	IconButton,
	Menu,
	MenuItem,
	Switch,
	TextField,
	Typography,
	Divider,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useStore } from "../../store/store";
import { MoreVertTwoTone, Save } from "@mui/icons-material";
import { useDeleteRecord } from "../../api/useDeleteRecord";
import { useSaveGridConfig } from "../../api/useSaveGridConfig";
import { RecordContext, RecordContextType } from "../record/Record";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import { RelatedInputField } from "../fieldsInputs/fields/RelatedInputField/RelatedInputField";
import { isRoleAdmin, isRoleDirector } from "../../utils/rolesUtils";
import { useUser } from "../../api/useAuth";

export const SaveAndUpdateSettings = () => {
	const { gridSettings, appDialog } = useStore();
	const [nameValue, setNameValue] = useState<string | undefined>();
	const [inputIsActive, setInputIsActive] = useState<boolean>();
	const { data: currentUser } = useUser();

	const entityForm = useForm<FieldValues>();
	const saveConfig = useSaveGridConfig(entityForm);
	const deleteSetting = useDeleteRecord();

	const sharedRoles = gridSettings?.selectedSettings?.shared_roles?.map((role) => ({
		name: role?.directus_roles_id?.name,
		id: role?.id,
		settings_id: role?.settings_id,
		directus_roles_id: {
			name: role?.directus_roles_id?.name,
			id: role?.directus_roles_id?.id,
		},
	}));

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleSaveAndClose = () => {
		saveConfig();
		handleClose();
	};

	const sharedRolesArray = gridSettings?.selectedSettings?.shared_roles;
	const hasSharedRoles = Array.isArray(sharedRolesArray) && sharedRolesArray.length > 0;

	const currentUserRoleId = currentUser?.role?.id;
	const canShareView = isRoleDirector(currentUserRoleId) || isRoleAdmin(currentUserRoleId);

    const userCreated = gridSettings?.selectedSettings?.user_created;
    const viewCreatedByUser = typeof userCreated === "string" ? userCreated : userCreated?.id ?? null;
	const isViewCreatedByCurrentUser = viewCreatedByUser === currentUser?.id;

	return (
		<Box sx={{ ml: 2 }}>
			<IconButton id="long-button" onClick={handleClick}>
				<MoreVertTwoTone />
			</IconButton>
			<Menu id="long-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Typography>Marcar como default</Typography>
						<Switch
							checked={gridSettings?.selectedSettings?.isActive || false}
							onChange={() =>
								gridSettings.selectedSettings &&
								gridSettings.setGridSettings({
									selectedSettings: {
										...gridSettings.selectedSettings,
										isActive: !gridSettings.selectedSettings?.isActive,
									},
								})
							}
						/>
					</Box>
				</MenuItem>
				<MenuItem disabled={gridSettings?.selectedSettings?.isSystem || !isViewCreatedByCurrentUser} onClick={handleSaveAndClose}>
					Guardar
				</MenuItem>

				<Box>
					<Button
						variant="text"
						color="inherit"
						sx={{ pl: 4, mb: 1, fontSize: 16 }}
						onClick={() => {
							setInputIsActive(!inputIsActive);
						}}
					>
						Guardar como novo
					</Button>
					{inputIsActive && (
						<Box sx={{ display: "flex", alignItems: "center", pl: 4, pb: 2, pr: 4, gap: 2 }}>
							<TextField
								variant="outlined"
								size="small"
								label="Nome da configuração"
								value={nameValue || ""}
								onKeyDown={(e) => e.stopPropagation()}
								onChange={(e) => {
									setNameValue(e.target.value);
								}}
							/>
							<IconButton
								color="primary"
								onClick={() => {
									saveConfig(nameValue, true);
									setInputIsActive(false);
								}}
							>
								<Save fontSize="medium" />
							</IconButton>
						</Box>
					)}
					{canShareView && isViewCreatedByCurrentUser && (
						<MenuItem
							disabled={gridSettings?.selectedSettings?.isSystem}
							onClick={() =>
								appDialog.createDialog({
									title: "Partilhar vista com Perfis",
									contentAndActions: (
										<ShareViewWithRolesDialog entityForm={entityForm} sharedRoles={sharedRoles} saveConfig={saveConfig} closeViewMenu={handleClose} />
									),
								})
							}
						>
							{hasSharedRoles ? "Gerir partilha de vista" : "Partilhar vista"}
						</MenuItem>
					)}
					<MenuItem
						disabled={gridSettings?.selectedSettings?.isSystem || !isViewCreatedByCurrentUser}
						onClick={() => {
							if (hasSharedRoles) {
								const shouldDelete = window.confirm(
									"Esta vista está partilhada com outros utilizadores. Ao eliminar, eles perderão o acesso a esta vista. Deseja continuar?"
								);
								if (!shouldDelete) return;
							}

							gridSettings.selectedSettings &&
								deleteSetting.mutate({
									entity: "settings",
									id: gridSettings?.selectedSettings?.id,
								});
						}}
					>
						Apagar
					</MenuItem>
				</Box>
			</Menu>
		</Box>
	);
};

const ShareViewWithRolesDialog = ({
	entityForm,
	sharedRoles,
	saveConfig,
	closeViewMenu
}: {
	entityForm: UseFormReturn<FieldValues>;
	sharedRoles: any;
	saveConfig: () => void;
	closeViewMenu: () => void;
}) => {
	const { appDialog, gridSettings } = useStore();

	const recordContextValue = useMemo<RecordContextType | null>(() => {
		if (!gridSettings?.selectedSettings) return null;

		return {
			entity: "settings",
			formatedFields: [],
			id: gridSettings.selectedSettings.id,
			item: gridSettings.selectedSettings,
			type: "update",
			collection: { collection: "settings" } as any,
			entityForm: entityForm as any,
		};
	}, [gridSettings?.selectedSettings]);

	const handleClean = () => {
		appDialog.toggleIsOpen();
		entityForm.resetField("shared_roles");
		entityForm.setValue("multiple_shared_roles", {
			create: [],
			update: [],
			delete: [],
		});
	};
	const handleSaveAndClose = () => {
		saveConfig();
		appDialog.toggleIsOpen();
		entityForm.setValue("multiple_shared_roles", {
			create: [],
			update: [],
			delete: [],
		});
		closeViewMenu();
	};

	const isSystem = gridSettings?.selectedSettings?.isSystem;

	return isSystem ? (
		<>
		<DialogContent>
						<Divider />
			<Typography variant="body1" sx={{ mt: 2, mb: 3, color: "text.secondary" }}>
				A vista Default não pode ser partilhada.<br /> <br /> Por favor, crie uma vista nova ("Guardar como novo") para a poder partilhar com os perfis desejados.
			</Typography>
		</DialogContent>
		<DialogActions>
				<Button onClick={appDialog.toggleIsOpen} color="secondary">
					Cancelar
				</Button>
			</DialogActions>
		</>
	) : (
		<>
			<DialogContent sx={{ pb: 0, overflow: "visible" }}>
				<Divider />
				<Box sx={{ mt: 2, mb: 3, overflow: "visible" }}>
					<RecordContext.Provider value={recordContextValue}>
						<RelatedInputField
							field={{
								field: "shared_roles",
								settings: {
									relatedTable: "directus_roles",
									display_options: {
										template: "{{name}}",
									},
									interface: "list-m2m",
									type: "alias",
								},
							}}
							value={sharedRoles || [{ id: "", name: "" }]}
							entityForm={entityForm}
							extraOptions={{
								field: "shared_roles",
								relatedTable: "directus_roles",
								displayTemplate: "{{name}}",
								enabled: true,
								sort: "name",
								disableClearable: true,
								filter: {
									_and: [
										{ name: { _neq: "Clients" } },
										{ name: { _ncontains: "Bot" } },
										{ name: { _neq: "Assistente Virtual" } },
									],
								},
							}}
						/>
					</RecordContext.Provider>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClean} color="secondary">
					Cancelar
				</Button>
				<Button onClick={handleSaveAndClose}>Guardar</Button>
			</DialogActions>
		</>
	);
};
